import { Badge, Button, Card, CardContent, Input, Modal, Select, Spinner } from '@crm/ui';
import { ChevronRight, Pencil, Plus, Star, Trash2, UserRound, X } from 'lucide-react';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Autocomplete,
  type AutocompleteOption,
  Avatar,
  ConfirmDialog,
  EmptyState,
  FilterBar,
  PageHeader,
  Pagination,
} from '@/components';
import { useDebounce, useDocumentTitle } from '@/hooks';
import { useCan, useSession } from '@/modules/auth/hooks';
import { toErrorMessage } from '@/modules/auth/utils';
import { type CustomerSegment, useCustomers, useDeleteCustomer, useUpdateCustomer } from '../api';
import { CustomerForm } from '../components/customer-form';

/**
 * O que cada recorte do painel vira aqui. A chave e o que viaja na URL, em
 * portugues, porque e um endereco que a pessoa ve e compartilha.
 */
const SEGMENT_BY_PARAM: Record<string, CustomerSegment | undefined> = {
  'novos-mes': 'new-month',
  'novos-ano': 'new-year',
  'retornos-trimestre': 'returned-quarter',
  'sem-retorno-trimestre': 'not-returned-quarter',
};

const SEGMENT_LABEL: Record<CustomerSegment, string> = {
  'new-month': 'Clientes novos no mes',
  'new-year': 'Clientes novos no ano',
  'returned-quarter': 'Retornos no trimestre',
  'not-returned-quarter': 'Nao retornaram no trimestre',
};

export function CustomersPage() {
  useDocumentTitle('Clientes');

  const canWrite = useCan('customers', 'WRITE');
  const { user } = useSession();
  const units = user?.units ?? [];

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [search, setSearch] = useState('');
  const [selectedUnits, setSelectedUnits] = useState<AutocompleteOption[]>([]);
  const [status, setStatus] = useState<'all' | 'active' | 'inactive'>('all');
  const [favorites, setFavorites] = useState(false);
  const debouncedSearch = useDebounce(search, 300);

  // O painel manda o recorte pela URL; a tela mostra qual e e como sair dele.
  const [params, setParams] = useSearchParams();
  const segment = SEGMENT_BY_PARAM[params.get('segmento') ?? ''];

  const customers = useCustomers({
    page,
    perPage,
    search: debouncedSearch,
    unitId: selectedUnits[0]?.id,
    status,
    favorites,
    segment,
  });
  const updateCustomer = useUpdateCustomer();
  const deleteCustomer = useDeleteCustomer();

  const actionError = updateCustomer.error ?? deleteCustomer.error;

  function resetToFirstPage() {
    setPage(1);
  }

  const [pendingRemoval, setPendingRemoval] = useState<{ id: string; name: string } | null>(null);
  /** Cadastro em modal: `id` vazio e um cliente novo. */
  const [editing, setEditing] = useState<{ id?: string; isFavorite?: boolean } | null>(null);
  const editingCustomer = editing?.id
    ? customers.data?.data.find((customer) => customer.id === editing.id)
    : undefined;

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<UserRound />}
        title="Clientes"
        description="Base de clientes da empresa, com busca, filtros e favoritos."
        actions={
          canWrite ? (
            <Button onClick={() => setEditing({ id: undefined })}>
              <Plus className="h-4 w-4" />
              <span>
                Novo<span className="hidden sm:inline"> cliente</span>
              </span>
            </Button>
          ) : null
        }
      />

      {/* Chegou do painel: a tela diz qual recorte esta aberto e como sair
          dele - sem isso a lista viria menor sem explicacao. */}
      {segment ? (
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="secondary">{SEGMENT_LABEL[segment]}</Badge>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 text-muted-foreground"
            onClick={() => {
              params.delete('segmento');
              setParams(params);
              resetToFirstPage();
            }}
          >
            <X className="h-4 w-4" />
            Limpar recorte
          </Button>
        </div>
      ) : null}

      {actionError ? (
        <p className="text-sm text-destructive">{toErrorMessage(actionError)}</p>
      ) : null}

      <Card variant="flush">
        {/* Filtro e lista no mesmo quadro: sao a mesma ferramenta, e separados
            o filtro parecia um bloco solto acima do conteudo. */}
        <CardContent className="space-y-6">
          {/* No celular sai so a busca com o botao ao lado: unidade, situacao e
              favoritos apareciam antes da lista e empurravam os clientes para
              fora da primeira tela. */}
          <FilterBar
            className="sm:flex sm:flex-row sm:flex-wrap sm:items-center"
            activeCount={selectedUnits.length + (status !== 'all' ? 1 : 0) + (favorites ? 1 : 0)}
            search={
              <Input
                className="sm:max-w-xs"
                placeholder="Buscar por nome, e-mail ou telefone"
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  resetToFirstPage();
                }}
              />
            }
          >
            {/* Unidade primeiro, como em toda tela do sistema - e so para quem
                atende mais de uma. */}
            {units.length > 1 ? (
              <Autocomplete
                className="sm:order-first sm:w-52"
                multiple
                emptyLabel="Todas as unidades"
                placeholder="Filtrar unidade"
                url="/api/v1/units"
                value={selectedUnits}
                onChange={(chosen) => {
                  setSelectedUnits(chosen);
                  setPage(1);
                }}
              />
            ) : null}

            <Select
              className="sm:max-w-40"
              value={status}
              onChange={(event) => {
                setStatus(event.target.value as typeof status);
                resetToFirstPage();
              }}
            >
              <option value="all">Todos</option>
              <option value="active">Ativos</option>
              <option value="inactive">Inativos</option>
            </Select>

            <Button
              variant={favorites ? 'default' : 'outline'}
              onClick={() => {
                setFavorites((current) => !current);
                resetToFirstPage();
              }}
            >
              <Star className="h-4 w-4" />
              Favoritos
            </Button>
          </FilterBar>

          {customers.isLoading ? (
            <div className="flex justify-center py-8">
              <Spinner className="h-6 w-6 text-muted-foreground" />
            </div>
          ) : customers.isError ? (
            <p className="text-sm text-destructive">{toErrorMessage(customers.error)}</p>
          ) : customers.data && customers.data.data.length > 0 ? (
            <ul className="divide-y divide-border">
              {customers.data.data.map((customer) => (
                <li
                  key={customer.id}
                  // Acoes na mesma linha do nome: empilhadas, cada cliente
                  // gastava duas alturas e a lista rendia metade na tela.
                  className="flex items-center gap-2 py-3 first:pt-0 last:pb-0"
                >
                  {/* A linha toda abre o cadastro: no celular o alvo e o
                      registro inteiro, nao um icone de 44px no canto. */}
                  <button
                    type="button"
                    className="flex min-w-0 flex-1 items-center gap-3 text-left"
                    onClick={() => setEditing({ id: customer.id, isFavorite: customer.isFavorite })}
                  >
                    {/* Rosto antes do nome: numa base grande a foto e o que
                        identifica o cliente mais rapido que o texto. Sem foto,
                        as iniciais mantem a coluna alinhada. */}
                    <Avatar name={customer.name} image={customer.image} />

                    <span className="min-w-0 flex-1">
                      <span className="flex flex-wrap items-center gap-2">
                        <span className="font-medium">{customer.name}</span>
                        {customer.isFavorite ? (
                          <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
                        ) : null}
                        {customer.isActive ? null : <Badge variant="destructive">Inativo</Badge>}
                        {customer.unitName ? (
                          <Badge variant="outline">{customer.unitName}</Badge>
                        ) : null}
                      </span>
                      <span className="block truncate text-xs text-muted-foreground">
                        {[customer.email, customer.phone].filter(Boolean).join(' - ') ||
                          'Sem contato cadastrado'}
                      </span>
                    </span>
                  </button>

                  {/* No celular so a seta: as acoes vao para o rodape do
                      cadastro, que a linha ja abre. No desktop, onde sobra
                      largura, elas continuam a mao. */}
                  <ChevronRight
                    aria-hidden
                    className="h-5 w-5 shrink-0 text-muted-foreground sm:hidden"
                  />

                  {canWrite ? (
                    <div className="hidden shrink-0 items-center sm:flex">
                      <Button
                        variant="ghost"
                        size="icon"
                        title={customer.isFavorite ? 'Remover dos favoritos' : 'Favoritar'}
                        disabled={updateCustomer.isPending}
                        onClick={() =>
                          updateCustomer.mutate({
                            id: customer.id,
                            input: { isFavorite: !customer.isFavorite },
                          })
                        }
                      >
                        <Star
                          className={
                            customer.isFavorite
                              ? 'h-4 w-4 fill-amber-400 text-amber-400'
                              : 'h-4 w-4'
                          }
                        />
                      </Button>

                      <Button
                        variant="ghost"
                        size="icon"
                        title="Editar"
                        onClick={() => setEditing({ id: customer.id })}
                      >
                        <Pencil />
                      </Button>

                      <Button
                        variant="ghost"
                        size="icon"
                        title="Excluir"
                        disabled={deleteCustomer.isPending}
                        onClick={() => setPendingRemoval({ id: customer.id, name: customer.name })}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState icon={<UserRound />} title="Nenhum cliente encontrado" />
          )}
        </CardContent>
      </Card>

      <Pagination
        meta={customers.data?.meta}
        label="clientes"
        onChange={setPage}
        perPage={perPage}
        onPerPageChange={(value) => {
          setPerPage(value);
          setPage(1);
        }}
      />

      <Modal
        open={editing !== null}
        onClose={() => setEditing(null)}
        title={editing?.id ? 'Editar cliente' : 'Novo cliente'}
        size="large"
        // Num cliente que ja existe, favoritar e excluir moram aqui: e o que
        // tira a fileira de botoes de cada linha da lista no celular.
        actions={
          editingCustomer
            ? [
                {
                  label: 'Excluir',
                  icon: <Trash2 />,
                  variant: 'ghost',
                  disabled: deleteCustomer.isPending,
                  onClick: () => {
                    setPendingRemoval({ id: editingCustomer.id, name: editingCustomer.name });
                    setEditing(null);
                  },
                },
                {
                  label: editingCustomer.isFavorite ? 'Desfavoritar' : 'Favoritar',
                  icon: (
                    <Star
                      className={
                        editingCustomer.isFavorite ? 'fill-amber-400 text-amber-400' : undefined
                      }
                    />
                  ),
                  variant: 'outline',
                  disabled: updateCustomer.isPending,
                  onClick: () =>
                    updateCustomer.mutate({
                      id: editingCustomer.id,
                      input: { isFavorite: !editingCustomer.isFavorite },
                    }),
                },
                { label: 'Salvar', type: 'submit', formId: 'customer-form' },
              ]
            : [
                { label: 'Cancelar', variant: 'ghost', onClick: () => setEditing(null) },
                { label: 'Salvar', type: 'submit', formId: 'customer-form' },
              ]
        }
      >
        {editing ? (
          <CustomerForm
            id={editing.id}
            formId="customer-form"
            hideSubmit
            // A lista se atualiza sozinha: o hook do formulario invalida a
            // consulta ao salvar.
            onSaved={() => setEditing(null)}
          />
        ) : null}
      </Modal>

      <ConfirmDialog
        open={pendingRemoval !== null}
        title="Excluir cliente"
        message={`O cliente ${pendingRemoval?.name ?? ''} sai da base. Esta acao nao pode ser desfeita.`}
        loading={deleteCustomer.isPending}
        onClose={() => setPendingRemoval(null)}
        onConfirm={() => {
          if (pendingRemoval) deleteCustomer.mutate(pendingRemoval.id);
          setPendingRemoval(null);
        }}
      />
    </div>
  );
}
